import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import type { Notification } from "../types/notifications";
import notificationService from "../services/notificationService";
import { useAuth } from "./AuthContext";

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: Error | null;
  addNotification: (
    notification: Omit<Notification, "id" | "read" | "createdAt">,
  ) => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  clearNotification: (id: string) => Promise<void>;
  fetchNotifications: () => Promise<void>;
  refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, loading: authLoading, hasPermission } = useAuth();
  const canViewNotifications = hasPermission("notifications_view");
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const activeFetchRef = useRef<Promise<void> | null>(null);
  const lastFetchAtRef = useRef(0);

  const unreadCount = useMemo(
    () => notifications.filter((notification) => !notification.read).length,
    [notifications],
  );

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated || !canViewNotifications) return;
    if (activeFetchRef.current) return activeFetchRef.current;

    const request = (async () => {
      setIsLoading(true);
      setError(null);
      try {
        setNotifications(await notificationService.getNotifications());
        lastFetchAtRef.current = Date.now();
      } catch (requestError) {
        const normalizedError =
          requestError instanceof Error
            ? requestError
            : new Error("No se pudieron cargar las notificaciones");
        setError(normalizedError);
        throw requestError;
      } finally {
        setIsLoading(false);
      }
    })();

    activeFetchRef.current = request;
    try {
      await request;
    } finally {
      activeFetchRef.current = null;
    }
  }, [canViewNotifications, isAuthenticated]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !canViewNotifications) {
      setNotifications([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    void fetchNotifications().catch(() => undefined);
    const pollIntervalMs = 60000;
    const pollWhenVisible = () => {
      const isStale = Date.now() - lastFetchAtRef.current >= pollIntervalMs;
      if (document.visibilityState === "visible" && isStale) {
        void fetchNotifications().catch(() => undefined);
      }
    };
    const interval = window.setInterval(pollWhenVisible, pollIntervalMs);
    document.addEventListener("visibilitychange", pollWhenVisible);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", pollWhenVisible);
    };
  }, [authLoading, canViewNotifications, isAuthenticated, fetchNotifications]);

  const markAsRead = async (id: string) => {
    await notificationService.markAsRead(id);
    setNotifications((previous) =>
      previous.map((notification) =>
        notification.id === id ? { ...notification, read: true } : notification,
      ),
    );
  };

  const markAllAsRead = async () => {
    await notificationService.markAllAsRead();
    setNotifications((previous) =>
      previous.map((notification) => ({ ...notification, read: true })),
    );
    toast.success("Todas las notificaciones marcadas como leídas");
  };

  const clearNotification = async (id: string) => {
    await notificationService.deleteNotification(id);
    setNotifications((previous) =>
      previous.filter((notification) => notification.id !== id),
    );
    toast.success("Notificación eliminada");
  };

  const addNotification = async (
    notification: Omit<Notification, "id" | "read" | "createdAt">,
  ) => {
    const localNotification: Notification = {
      ...notification,
      id: crypto.randomUUID(),
      read: false,
      createdAt: new Date(),
    };
    setNotifications((previous) => [localNotification, ...previous]);
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        isLoading,
        error,
        addNotification,
        markAsRead,
        markAllAsRead,
        clearNotification,
        fetchNotifications,
        refreshNotifications: fetchNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications debe usarse dentro de NotificationProvider");
  }
  return context;
};
